import React, { useEffect, useState } from "react";
import className from "classnames";
import { Button, Close } from "@cmsgov/ds-medicare-gov";
import uniqueId from "lodash.uniqueid";
import { useTranslate } from "../helpers";
import classNames from "classnames";

interface TooltipProps {
  title: string;
  triggerText: string | JSX.Element;
  triggerClassName?: string;
  inline?: boolean;
  onClose?: () => void;
  onOpen?: () => void;
}

const Tooltip: React.FunctionComponent<TooltipProps> = ({
  title,
  triggerText,
  triggerClassName,
  onClose,
  onOpen,
  inline,
  children,
}) => {
  const [buttonId, setButtonId] = useState("");
  const [tooltipId, setTooltipId] = useState("");
  let trigger: HTMLInputElement;
  const [isOpen, setIsOpen] = useState(false);
  const t = useTranslate();

  const close = (): void => {
    trigger?.focus();
    setIsOpen(false);
    onClose && onClose();
  };

  const open = (): void => {
    if (!isOpen) {
      setIsOpen(true);
      onOpen && onOpen();
    }
  };

  const clickHandler = (e: MouseEvent): void => {
    const target = e.target as HTMLElement;
    if (
      (isOpen &&
        (target.matches(".Tooltip__trigger") ||
          target.closest(".Tooltip__trigger"))) ||
      !target.closest(".Tooltip")
    ) {
      close();
    }
  };

  const upHandler = (e: KeyboardEvent): void => {
    const ESCAPE_KEY = 27;
    if (e.keyCode === ESCAPE_KEY) {
      if (
        (trigger && trigger.id === document.activeElement?.id) ||
        document.activeElement?.closest(`#${tooltipId}`)
      ) {
        close();
      }
    }
  };

  useEffect(() => {
    const id = uniqueId();
    setButtonId(`mct_tooltip_button_${id}`);
    setTooltipId(`mct_tooltip_${id}`);
  }, []);

  useEffect(() => {
    if (tooltipId) {
      if (isOpen) {
        trigger?.focus();
        document.addEventListener("keyup", upHandler);
        document.addEventListener("click", clickHandler);
      } else {
        document.removeEventListener("keyup", upHandler);
        document.removeEventListener("click", clickHandler);
      }
    }

    return (): void => {
      document.removeEventListener("keyup", upHandler);
      document.removeEventListener("click", clickHandler);
    };
  }, [tooltipId, isOpen]);

  const triggerClass = classNames(
    "Tooltip__trigger",
    triggerClassName,
    isOpen ? "Tooltip__trigger--open" : ""
  );
  const tooltipClass = classNames("Tooltip", {
    "ds-u-display--inline-block": inline,
  });

  return (
    <div className={tooltipClass}>
      <Button
        id={buttonId}
        onClick={open}
        className={triggerClass}
        inputRef={(ref: HTMLInputElement): HTMLInputElement => {
          trigger = ref;
          return ref;
        }}
      >
        <span>{triggerText}</span>
      </Button>
      <div
        id={tooltipId}
        className={className("Tooltip__container", { "Tooltip--open": isOpen })}
        role="tooltip"
        aria-hidden={!isOpen}
      >
        <div className="Tooltip__header">
          <h2 className="ds-h4">{title}</h2>
          <Button
            variation="transparent"
            size="small"
            onClick={close}
            aria-label={t("close")}
          >
            <Close />
          </Button>
        </div>
        <div className="Tooltip__body">{children}</div>
      </div>
    </div>
  );
};

export default Tooltip;
